import type { ApiEndpointBuilder } from "../apiEndpointHelpers";
import type {
  AdminV2SupportTestResetCapabilityResponse,
  AdminV2SupportTestResetLookupResponse,
  AdminV2SupportTestResetPreviewResponse,
  AdminV2SupportTestResetPurgeRequest,
  AdminV2SupportTestResetPurgeRunResponse,
  AdminV2SupportTestResetPurgeStartResponse,
} from "./adminV2SupportTestResetTypes";

/**
 * RTK Query endpoints for the staging-only "Test data reset" support tool.
 *
 * @remarks
 * The lookup query is tagged with a single static "LOOKUP" tag (not keyed by
 * email) so a successful purge invalidates whichever search is currently on
 * screen without extra plumbing in the route component.
 */
export const buildAdminV2SupportTestResetEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2SupportTestResetCapability: builder.query<
    AdminV2SupportTestResetCapabilityResponse,
    void
  >({
    query: () => ({ url: "/admin/v2/support/test-reset/capability" }),
    keepUnusedDataFor: 60,
  }),

  getAdminV2SupportTestResetLookup: builder.query<
    AdminV2SupportTestResetLookupResponse,
    { email: string }
  >({
    query: ({ email }) => ({
      url: "/admin/v2/support/test-reset/lookup",
      params: { email },
    }),
    providesTags: [{ type: "AdminV2SupportTestReset", id: "LOOKUP" }],
  }),

  getAdminV2SupportTestResetPreview: builder.query<
    AdminV2SupportTestResetPreviewResponse,
    { organisationId: string; userId?: string }
  >({
    query: ({ organisationId, userId }) => ({
      url: "/admin/v2/support/test-reset/preview",
      params: userId ? { organisationId, userId } : { organisationId },
    }),
  }),

  // The purge runs in the background on the API (a synced company can take
  // well over the 60 s request timeout), so the mutation only starts it and
  // the dialog polls the run until it finishes. The lookup cache is
  // invalidated by the dialog on completion, not here, so the list does not
  // refresh while the organisation still exists.
  purgeAdminV2SupportTestReset: builder.mutation<
    AdminV2SupportTestResetPurgeStartResponse,
    AdminV2SupportTestResetPurgeRequest
  >({
    query: (body) => ({
      url: "/admin/v2/support/test-reset/purge",
      method: "POST",
      data: body,
    }),
  }),

  getAdminV2SupportTestResetPurgeRun: builder.query<
    AdminV2SupportTestResetPurgeRunResponse,
    { runId: string }
  >({
    query: ({ runId }) => ({
      url: `/admin/v2/support/test-reset/purge-runs/${encodeURIComponent(runId)}`,
    }),
    keepUnusedDataFor: 0,
  }),
});
